import { useMutation } from "@tanstack/react-query";
import { Button, Form, FormProps, Input, Switch } from "antd";
const { Password } = Input;
import { Link, useNavigate } from "react-router-dom";
import { CustomizeRequiredMark } from "~/components/common/custom-required-mark";
import { useNotification } from "~/hooks";
import { authServices } from "~/services/auth";
import { useAuth } from "~/store";
import { Role } from "~/utils/constants";

type FieldType = {
  username: string;
  password: string;
};

export const LoginView = () => {
  const navigate = useNavigate();
  const { login, setMe } = useAuth();

  const api = useNotification();

  const { mutate, isPending } = useMutation({
    mutationFn: authServices.login,
    onSuccess: async (tokens) => {
      login(tokens);
      const user = await authServices.getMe();
      setMe(user);
      switch (user.role) {
        case Role.CLIENT:
          navigate("/overview");
          break;
        case Role.ADMIN || Role.SUPER_ADMIN:
          navigate("/");
          break;
        case Role.STAFF:
          navigate("/assignments");
          break;
      }
    },
    onError: (error: any) => {
      api.error({
        message: "Login failed",
        description: error.response?.data?.message || "Invalid credentials",
      });
    },
  });

  const onFinish: FormProps<FieldType>["onFinish"] = (values) => {
    mutate(values);
  };

  return (
    <main className="flex-1 flex flex-col items-center justify-center px-4">
      {/* Content */}
      <div className="max-w-6xl mx-auto flex flex-col items-center">
        {/* Brand Name - Variation 3 with App */}
        <div className="mb-12 flex items-center justify-center gap-3">
          <h1 
            className="text-5xl sm:text-6xl text-black"
            style={{
              fontFamily: "'Oswald', sans-serif",
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase'
            }}
          >
            Art Handler
          </h1>
          <span 
            className="text-lg sm:text-xl text-black"
            style={{
              fontFamily: "'Oswald', sans-serif",
              fontWeight: 400,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              alignSelf: 'flex-end',
              paddingBottom: '0.3em'
            }}
          >
            App
          </span>
        </div>

        {/* Sign In Card - Neo Brutalist Style */}
        <div className="w-full max-w-md">
          <div className="border-2 border-black bg-[#faf8f3] p-8 sm:p-10">

            <Form
              layout="vertical"
              onFinish={onFinish}
              className="space-y-6"
              requiredMark={CustomizeRequiredMark}
            >
              <Form.Item
                name="username"
                rules={[
                  { required: true, message: "Please input your username!" },
                ]}
                label={
                  <span className="text-xs font-bold uppercase tracking-wide text-black" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    User 
                  </span>
                }
                className="mb-6"
              >
                <Input
                  size="large"
                  id="username"
                  type="text"
                  placeholder="Enter your username"
                  className="border-2 border-black px-3 py-2 font-bold text-sm uppercase bg-[#faf8f3] text-black placeholder:text-black/50 focus:border-black focus:ring-0"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                />
              </Form.Item>

              <Form.Item
                name="password"
                label={
                  <span className="text-xs font-bold uppercase tracking-wide text-black" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    Password
                  </span>
                }
                rules={[{ required: true }]}
                className="mb-6"
              >
                <Password
                  size="large"
                  id="password"
                  placeholder="Enter your password"
                  className="border-2 border-black px-3 py-2 font-bold text-sm uppercase bg-[#faf8f3] text-black placeholder:text-black/50 focus:border-black focus:ring-0 [&_input]:font-bold [&_input]:text-sm [&_input]:bg-[#faf8f3] [&_input]:text-black [&_input]:placeholder:text-black/50 [&_input]:normal-case"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                />
              </Form.Item>

              <div className="flex items-center pt-2 mb-6">
                <div className="flex items-center">
                  <Switch 
                    id="remember"
                    className="[&_.ant-switch-inner]:bg-[#faf8f3] [&_.ant-switch-checked]:bg-black [&_.ant-switch-handle]:border-2 [&_.ant-switch-handle]:border-black"
                  />
                  <label
                    htmlFor="remember"
                    className="ml-3 text-sm font-bold uppercase tracking-wide text-black"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    Remember me
                  </label>
                </div>
              </div>

              <Button
                loading={isPending}
                htmlType="submit"
                size="large"
                className="w-full border-2 border-black bg-black text-white px-4 py-3 font-bold text-sm uppercase tracking-wide hover:opacity-90 transition-all"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                {isPending ? "Authenticating..." : "Sign In"}
              </Button>

              <div className="text-center mt-4">
                <Link
                  to="/request-password-reset"
                  className="text-sm font-bold uppercase tracking-wide text-black hover:opacity-70 transition-opacity border-b-2 border-transparent hover:border-black"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  Forgot Password?
                </Link>
              </div>

              <div className="text-center text-sm font-medium text-black pt-6 border-t-2 border-black mt-6" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                Need help?{" "}
                <Link
                  to="#"
                  className="font-bold uppercase tracking-wide text-black hover:opacity-70 transition-opacity border-b-2 border-transparent hover:border-black"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  Contact Support
                </Link>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </main>
  );
};
